import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  CircularProgress,
  Chip,
} from '@mui/material';
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  Language as LanguageIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';

import PublisherForm from './PublisherForm';

import { useNavigate } from 'react-router-dom';
import { deletePublisher, setPage, setRowsPerPage } from 'features/publishers/slices/publishersSlice';
import { AppDispatch, RootState } from 'store';
import { Publisher } from 'types/publisher.types';
import ConfirmDialog from './ConfirmDialog';
import SmartLogo from 'components/common/SmartLogo';

// Reusable ClickableCell component for better code organization and accessibility
interface ClickableCellProps {
  children: React.ReactNode;
  onClick: () => void;
  ariaLabel?: string;
  [key: string]: any; // Allow additional props
}

const ClickableCell = React.forwardRef<HTMLTableCellElement, ClickableCellProps>(({ 
  children, 
  onClick, 
  ariaLabel,
  ...props 
}, ref) => (
  <TableCell 
    ref={ref}
    onClick={onClick}
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    }}
    role="button"
    aria-label={ariaLabel}
    sx={{ 
      cursor: 'pointer',
      '&:hover': { 
        backgroundColor: 'action.hover',
      },
      position: 'relative',
      overflow: 'visible',
      transition: 'background-color 0.2s',
    }}
    {...props}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', overflow: 'visible' }}>
      <Box sx={{ flexGrow: 1, overflow: 'visible' }}>{children}</Box>
      <ChevronRightIcon 
        className="clickable-indicator"
        sx={{ 
          opacity: 0, 
          transition: 'opacity 0.2s',
          ml: 1,
        }} 
        fontSize="small" 
        color="action" 
      />
    </Box>
  </TableCell>
));

ClickableCell.displayName = 'ClickableCell';

interface PublishersTableProps {
  onPublisherCreated?: (publisher: Publisher) => void;
}

const PublishersTable: React.FC<PublishersTableProps> = ({ onPublisherCreated }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { filteredItems, isLoading, pagination } = useSelector((state: RootState) => state.publishers);
  
  const [openForm, setOpenForm] = useState(false);
  const [selectedPublisher, setSelectedPublisher] = useState<Publisher | undefined>(undefined);
  const [isEdit, setIsEdit] = useState(false);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [publisherToDelete, setPublisherToDelete] = useState<Publisher | null>(null);

  const handleChangePage = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setRowsPerPage(parseInt(event.target.value, 10)));
  };

  const handleAddClick = () => {
    setIsEdit(false);
    setSelectedPublisher(undefined);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
  };

  const handleDeleteClick = (publisher: Publisher) => {
    setPublisherToDelete(publisher);
    setOpenConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    if (publisherToDelete) {
      try {
        await dispatch(deletePublisher(publisherToDelete.id));
        setOpenConfirmDelete(false);
        setPublisherToDelete(null);
      } catch (error) {
        console.error('Error deleting publisher:', error);
      }
    }
  };

  const handleCancelDelete = () => {
    setOpenConfirmDelete(false);
    setPublisherToDelete(null);
  };

  const handleViewDetail = (publisherId: string) => {
    navigate(`/publishers/${publisherId}`);
  };

  const handlePublisherCreated = (publisher: Publisher) => {
    // Notify parent to open WebsiteForm
    onPublisherCreated?.(publisher);
  };

  // Get currently displayed items based on pagination
  const displayedItems = filteredItems.slice(
    pagination.page * pagination.rowsPerPage,
    pagination.page * pagination.rowsPerPage + pagination.rowsPerPage
  );
  
  return (
    <Box sx={{ width: '100%', overflow: 'visible' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" component="h2">
          Publishers
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          disabled={isLoading}
        >
          Add Publisher
        </Button>
      </Box>
      
      <Paper sx={{ width: '100%', mb: 2, overflow: 'visible' }}>
        <TableContainer sx={{ overflow: 'visible' }}>
          <Table size="medium" stickyHeader sx={{ overflow: 'visible' }}>
            <TableHead>
              <TableRow>
                <TableCell>Publisher</TableCell>
                <TableCell>Tagline</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Website</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : displayedItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" color="text.secondary">
                      No publishers found.
                    </Typography>
                    <Button
                      variant="text"
                      startIcon={<AddIcon />}
                      onClick={handleAddClick}
                      sx={{ mt: 1 }}
                    >
                      Add your first publisher
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                displayedItems.map((publisher) => (
                  <TableRow 
                    key={publisher.id} 
                    hover 
                    sx={{
                      // Add subtle highlight effect for the entire row
                      '&:hover .clickable-indicator': {
                        opacity: 0.5,
                      }
                    }}
                  >
                    {/* Publisher Logo cell - now clickable with tooltip */}
                    <Tooltip title="View publisher details" arrow>
                      <ClickableCell 
                        onClick={() => handleViewDetail(publisher.id)}
                        ariaLabel={`View details for ${publisher.name}`}
                      >
                        <SmartLogo logoUrl={publisher.logoUrl!} name={publisher.name} />
                      </ClickableCell>
                    </Tooltip>
                    
                    {/* Tagline cell - now clickable with tooltip */}
                    <Tooltip title="View publisher details" arrow>
                      <ClickableCell 
                        onClick={() => handleViewDetail(publisher.id)}
                        ariaLabel={`View details for ${publisher.name}`}
                      >
                        {publisher.tagline || '-'}
                      </ClickableCell>
                    </Tooltip>
                    
                    {/* Location cell - now clickable with tooltip */}
                    <Tooltip title="View publisher details" arrow>
                      <ClickableCell 
                        onClick={() => handleViewDetail(publisher.id)}
                        ariaLabel={`View details for ${publisher.name}`}
                      >
                        {publisher.businessAddress?.city && publisher.businessAddress?.state ? (
                          `${publisher.businessAddress.city}, ${publisher.businessAddress.state}`
                        ) : '-'}
                      </ClickableCell>
                    </Tooltip>
                    
                    {/* Website column - not clickable for details, keeps original behavior */}
                    <TableCell>
                      {publisher.websiteUrl ? (
                        <a 
                          href={publisher.websiteUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none' }}
                        >
                          <Chip
                            icon={<LanguageIcon fontSize="small" />}
                            label="Visit"
                            size="small"
                            color="primary"
                            variant="outlined"
                            clickable
                          />
                        </a>
                      ) : '-'}
                    </TableCell>
                    
                    {/* Actions column - not clickable for details, keeps original behavior */}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title="Delete">
                          <IconButton color="error" onClick={() => handleDeleteClick(publisher)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredItems.length}
          rowsPerPage={pagination.rowsPerPage}
          page={pagination.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      
      {/* Publisher Form Dialog */}
      <PublisherForm
        open={openForm}
        onClose={handleCloseForm}
        publisher={selectedPublisher}
        isEdit={isEdit}
        onPublisherCreated={handlePublisherCreated}
      />
      
      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={openConfirmDelete}
        title="Confirm Delete"
        message={`Are you sure you want to delete ${publisherToDelete?.name}? This will also affect any websites and journalists associated with this publisher.`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </Box>
  );
};

export default PublishersTable;